import { apiGet, apiPost } from "../_apiShim";
import { EmailSentDTOStatistics } from "@/types/EmailSentDTOStatistics";
import { Filter } from "@/types/Filter";
import { EmailSentDTO } from "@/types/EmailsSentDTO";

const BASE_PATH = "/api/v1/email-sent/";

/**
 * Distinct campaign labels (sendName) for an event — options for the guest-list
 * "Kampagne" filter (Field.optionSource "campaigns"). Best-effort: returns [] on
 * error so the picker degrades to empty rather than breaking the filter UI.
 */
export async function getEventCampaigns(eventId: string): Promise<string[]> {
  try {
    return await apiGet<string[]>(
      `${BASE_PATH}event/${eventId}/campaigns`,
      { "Cache-Control": "no-store" },
      true
    );
  } catch {
    return [];
  }
}

export async function getEmailSent(
  eventId: string,
  emailSentId: string,
  fields: string
): Promise<EmailSentDTOStatistics> {
  const url = `${BASE_PATH}${eventId}/${emailSentId}?fields=${encodeURIComponent(
    fields
  )}`;

  return await apiGet<EmailSentDTOStatistics>(
    url,
    {
      "Cache-Control": "no-store",
    },
    true
  );
}

/**
 * Get EmailSent DTOs with joined invitee data, filtering, searching, sorting, and pagination
 */
export async function getEmailSentDTOsByEvent(
  eventId: string,
  fields: string,
  filters: Filter[],
  searchTerm: string,
  sortBy: string,
  sortOrder: "asc" | "desc",
  page: number,
  pageSize: number,
  /**
   * PB-TG-7 — epoch millis captured when the caller started paging this list.
   * Rows written after that instant are excluded server-side, so an insert
   * between two page reads can no longer shift every later offset and make the
   * boundary row appear twice (or vanish).
   *
   * Optional: omitted means unbounded, i.e. exactly today's behaviour.
   */
  asOf?: number,
): Promise<EmailSentDTO[]> {
  const queryParams = new URLSearchParams({
    fields: fields,
    search: searchTerm,
    sortBy: sortBy,
    sortOrder: sortOrder,
    page: page.toString(),
    pageSize: pageSize.toString(),
    ...(asOf !== undefined ? { asOf: String(asOf) } : {}),
  });

  const url = `${BASE_PATH}event/${eventId}/dto?${queryParams.toString()}`;

  // No catch-return-[] here: swallowing made a 5xx indistinguishable from
  // "no mails" before the handler's ServerError mapping could run (BUGS.md
  // M4/M12, FIXED 2026-07-05 W2-E). The handler wrapper catches and returns
  // a ServerError value.
  return await apiPost<EmailSentDTO[]>(
    url,
    filters,
    {
      "Cache-Control": "no-store",
    },
    true
  );
}

/**
 * Count EmailSent DTOs with filtering and searching
 */
export async function countEmailSentDTOsByEvent(
  eventId: string,
  filters: Filter[],
  searchTerm: string,
  /**
   * PB-TG-7 — the SAME paging watermark the list takes, so the total describes
   * the set the pages are drawn from. Optional; omitted = unbounded, i.e.
   * exactly today's behaviour.
   */
  asOf?: number
): Promise<number> {
  const queryParams = new URLSearchParams({
    search: searchTerm,
    ...(asOf !== undefined ? { asOf: String(asOf) } : {}),
  });

  const url = `${BASE_PATH}event/${eventId}/dto/count?${queryParams.toString()}`;

  // See getEmailSentDTOsByEvent — the handler maps failures to ServerError.
  return await apiPost<number>(url, filters, {
    "Cache-Control": "no-store",
  });
}
